import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, FileText, ChevronRight, ArrowLeft, Calendar,
  Loader2, Shield, Link2, Send, Users, History, Copy, MessageSquare, ShieldCheck
} from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { getAssignments, getDrafts, analyzeGuardDraft, submitTeacherFeedback, updateDraftStage, returnForRevision, getMyClasses, getCoachMessages } from '../../lib/api';
import { DraftyMascot } from '../DraftyMascot';
import { DraftTrack } from '../DraftTrack';
import { CreateAssignmentModal } from './CreateAssignmentModal';
import { AccommodationsPanel } from './AccommodationsPanel';
import LiveDraftBoard from './LiveDraftBoard';
import { AuthenticityReport } from './AuthenticityReport';
import { toast } from 'sonner';
import { DemoChip } from '../ui/demo-chip';
import { studentLabel } from '../../lib/format';

// Read-only coach transcript for a draft (Q10). Teacher opens it in the draft
// drilldown; RLS scopes what the endpoint returns. Zero new design — existing tokens.
const CoachTranscript = ({ draftId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null && !loading) {
      setLoading(true);
      try {
        const data = await getCoachMessages(draftId);
        setMessages(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load coach transcript:', e);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="mt-3 border-t border-slate-700 pt-3">
      <button
        data-testid={`coach-transcript-toggle-${draftId}`}
        onClick={toggle}
        className="text-slate-400 hover:text-flow-blue-hover text-xs flex items-center gap-1"
      >
        <MessageSquare size={14} /> {open ? 'Hide coach transcript' : 'View coach transcript'}
      </button>
      {open && (
        <div className="mt-3">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-xs py-2">
              <Loader2 size={14} className="animate-spin" /> Loading transcript…
            </div>
          ) : (messages && messages.length > 0) ? (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {messages.map((m) => (
                <div key={m.id} className={`text-sm ${m.role === 'assistant' ? 'text-slate-300' : 'text-white'}`}>
                  <span className={`status-chip ${m.role === 'assistant' ? 'status-chip--primary' : 'status-chip--gold'} mr-2`}>
                    {m.role === 'assistant' ? 'Drafty' : 'Student'}
                  </span>
                  <span className="whitespace-pre-wrap">{m.content}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-xs py-2">No coach conversation yet for this draft.</p>
          )}
        </div>
      )}
    </div>
  );
};

const STAGE_BADGE_STYLES = {
  prewrite: 'status-chip--gold',
  draft: 'status-chip--gold',
  revise: 'status-chip--gold',
  edit: 'status-chip--gold',
  publish: 'status-chip--gold',
};

const STAGE_LABELS = {
  prewrite: 'Prewrite',
  draft: 'Draft',
  revise: 'Revise',
  edit: 'Edit',
  publish: 'Publish',
};

export const AssignmentManager = () => {
  const { profile, isDemo } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentDrafts, setAssignmentDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [analyzingDraftId, setAnalyzingDraftId] = useState(null);
  const [trackDraftId, setTrackDraftId] = useState(null);
  const [reportDraftId, setReportDraftId] = useState(null);
  const [feedbackState, setFeedbackState] = useState({});
  const [savingFeedbackId, setSavingFeedbackId] = useState(null);
  const [resettingStageId, setResettingStageId] = useState(null);
  const [returningDraftId, setReturningDraftId] = useState(null);
  const [classesById, setClassesById] = useState({});

  useEffect(() => {
    const loadAssignments = async () => {
      try {
        const data = await getAssignments(profile?.id);
        setAssignments(data || []);
      } catch (error) {
        if (isDemo) {
          setAssignments([]);
        } else {
          console.error('Failed to load assignments:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    loadAssignments();
  }, [profile, isDemo]);

  // Class names for the per-assignment badge
  useEffect(() => {
    const loadClasses = async () => {
      try {
        const data = await getMyClasses();
        const cls = Array.isArray(data) ? data : data?.classes || [];
        const map = {};
        cls.forEach(c => { map[c.id] = c.class_name; });
        setClassesById(map);
      } catch (e) {
        console.error('Failed to load classes:', e);
      }
    };
    loadClasses();
  }, []);

  const studentLink = (assignment) =>
    `${window.location.origin}/join/assignment/${assignment.id}`;

  // Google's official share dialog — no OAuth scopes needed. The teacher
  // confirms the course and posts; students get the link in their stream.
  const postToClassroom = (assignment) => {
    const shareUrl =
      'https://classroom.google.com/share' +
      `?url=${encodeURIComponent(studentLink(assignment))}` +
      `&title=${encodeURIComponent(assignment.title || 'DraftFlow Assignment')}` +
      '&itemtype=assignment';
    const popup = window.open(shareUrl, '_blank', 'noopener,noreferrer,width=680,height=620');
    if (!popup) {
      navigator.clipboard.writeText(studentLink(assignment));
      toast.info('Popup blocked — student link copied. Paste it into Google Classroom.');
    }
  };

  useEffect(() => {
    const initial = {};
    assignmentDrafts.forEach(d => { initial[d.id] = d.teacher_feedback || ''; });
    setFeedbackState(initial);
  }, [assignmentDrafts]);

  const handleSaveFeedback = async (draftId) => {
    setSavingFeedbackId(draftId);
    try {
      await submitTeacherFeedback(draftId, feedbackState[draftId] || '');
      toast.success('Feedback saved');
    } catch (error) {
      console.error('Failed to save feedback:', error);
      toast.error('Failed to save feedback');
    } finally {
      setSavingFeedbackId(null);
    }
  };

  const handleResetStage = async (draftId, stage) => {
    setResettingStageId(draftId);
    try {
      await updateDraftStage(draftId, stage, true);
      // Refresh drafts to reflect new stage
      if (selectedAssignment) {
        await loadAssignmentDrafts(selectedAssignment.id);
      }
      toast.success(`Stage reset to ${STAGE_LABELS[stage]}`);
    } catch (error) {
      console.error('Failed to reset stage:', error);
      toast.error('Failed to reset stage');
    } finally {
      setResettingStageId(null);
    }
  };

  const handleReturnForRevision = async (draftId) => {
    setReturningDraftId(draftId);
    try {
      await returnForRevision(draftId);
      if (selectedAssignment) {
        await loadAssignmentDrafts(selectedAssignment.id);
      }
      toast.success('Draft returned to the student for revision');
    } catch (error) {
      console.error('Failed to return draft:', error);
      toast.error('Failed to return draft for revision');
    } finally {
      setReturningDraftId(null);
    }
  };

  const loadAssignmentDrafts = async (assignmentId) => {
    setLoadingDrafts(true);
    try {
      const draftsData = await getDrafts(null, assignmentId);
      setAssignmentDrafts(draftsData || []);
    } catch (error) {
      console.error('Failed to load drafts:', error);
      toast.error('Could not load student drafts for this assignment.');
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleSelectAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    loadAssignmentDrafts(assignment.id);
  };

  const handleAnalyzeDraft = async (draftId) => {
    setAnalyzingDraftId(draftId);
    try {
      await analyzeGuardDraft(draftId);
      toast.success('DraftGuard analysis complete.');
    } catch (error) {
      console.error('Guard analysis error:', error);
      toast.error('Analysis failed. The draft may need more content.');
    } finally {
      setAnalyzingDraftId(null);
    }
  };

  const reloadAssignments = async () => {
    try {
      const data = await getAssignments(profile?.id);
      setAssignments(data || []);
    } catch (error) {
      console.error('Failed to reload assignments:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading...</div>;
  }

  if (selectedAssignment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-to-assignments"
            aria-label="Back to assignments list"
            onClick={() => { setSelectedAssignment(null); setAssignmentDrafts([]); }}
            className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h2 className="font-heading text-2xl font-bold text-white">{selectedAssignment.title}</h2>
            <div className="flex items-center gap-4 mt-1">
              {selectedAssignment.due_date && (
                <span className="flex items-center gap-1 text-slate-400 text-sm">
                  <Calendar size={14} />
                  Due: {new Date(selectedAssignment.due_date).toLocaleDateString()}
                </span>
              )}
              {selectedAssignment.class_id && classesById[selectedAssignment.class_id] && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-teal-flow/20 text-teal-flow rounded text-xs">
                  <Users size={12} />
                  {classesById[selectedAssignment.class_id]}
                </span>
              )}
              {selectedAssignment.ccss_standards?.map((std, i) => (
                <span key={i} className="badge-ccss">{std}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Share */}
        <div className="flex gap-3">
          <button
            data-testid="post-to-classroom-btn"
            aria-label="Post assignment to Google Classroom"
            onClick={() => postToClassroom(selectedAssignment)}
            className="btn-purple px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
          >
            <Send size={16} />
            Post to Google Classroom
          </button>
          <button
            data-testid="copy-student-link-btn"
            aria-label="Copy student assignment link"
            onClick={() => {
              navigator.clipboard.writeText(studentLink(selectedAssignment));
              toast.success('Student link copied!');
            }}
            className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
          >
            <Link2 size={16} />
            Copy Student Link
          </button>
        </div>

        {/* Live roster status — who needs me right now (Phase 6 items 1-2) */}
        <LiveDraftBoard assignmentId={selectedAssignment.id} />

        {/* Assignment Prompt */}
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-3">Writing Prompt</h3>
          <p className="text-slate-300 text-sm leading-relaxed">{selectedAssignment.prompt}</p>
        </div>

        {/* Student Drafts */}
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="text-teal-flow" size={20} />
            Student Drafts
          </h3>
          {loadingDrafts ? (
            <div className="text-center py-8 text-slate-400">Loading drafts...</div>
          ) : assignmentDrafts.length === 0 ? (
            <div className="text-center py-8">
              <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
              <p className="text-slate-400 mb-4">Student drafts will appear here as they write. Share the assignment link to get them started.</p>
              <button
                data-testid="empty-copy-student-link"
                onClick={() => {
                  navigator.clipboard.writeText(studentLink(selectedAssignment));
                  toast.success('Student link copied!');
                }}
                className="btn-teal px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm"
              >
                <Copy size={16} />
                Copy student link
              </button>
            </div>
          ) : (
            <div className="space-y-3" role="list">
              {assignmentDrafts.map((draft) => (
                <div key={draft.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-white font-medium text-sm">{studentLabel(draft)}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`status-chip ${
                            draft.status === 'submitted' ? 'status-chip--success' :
                            'status-chip--primary'
                          }`}>
                            {draft.status === 'submitted' ? 'Submitted' : 'In Progress'}
                          </span>
                          <span className={`status-chip ${STAGE_BADGE_STYLES[draft.stage || 'prewrite']}`}>
                            {STAGE_LABELS[draft.stage || 'prewrite']}
                          </span>
                          <span className="text-slate-500 text-xs">{draft.word_count || 0} words</span>
                          {draft.last_saved && (
                            <span className="text-slate-500 text-xs">Last saved {new Date(draft.last_saved).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DemoChip label="Sign in to run DraftGuard">
                        <button
                          data-testid={`analyze-draft-${draft.id}`}
                          aria-label={`Analyze draft from ${studentLabel(draft)}`}
                          onClick={() => handleAnalyzeDraft(draft.id)}
                          disabled={analyzingDraftId === draft.id}
                          className="btn-purple px-3 py-1.5 rounded-lg flex items-center gap-2 text-xs disabled:opacity-50"
                        >
                          {analyzingDraftId === draft.id ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Shield size={14} />
                              Analyze
                            </>
                          )}
                        </button>
                      </DemoChip>
                      <button
                        data-testid={`view-history-${draft.id}`}
                        onClick={() => setTrackDraftId(draft.id)}
                        className="text-slate-400 hover:text-flow-blue-hover text-xs px-2 py-1.5 rounded-lg hover:bg-slate-700 flex items-center gap-1"
                        title="Replay how this draft was written"
                      >
                        <History size={14} /> Writing history
                      </button>
                      {/* Process evidence (Phase 6 item 5 / R8). Available on
                          every draft, not only submitted ones: a teacher who
                          returns a draft for revision is exactly the teacher
                          who still needs to see how it was written. */}
                      <button
                        data-testid={`process-report-${draft.id}`}
                        onClick={() => setReportDraftId(draft.id)}
                        className="text-slate-400 hover:text-flow-blue-hover text-xs px-2 py-1.5 rounded-lg hover:bg-slate-700 flex items-center gap-1"
                        title="See how this draft was written: sessions, revisions, and coach use"
                      >
                        <ShieldCheck size={14} /> Process report
                      </button>
                      {draft.status === 'submitted' && (
                        <button
                          data-testid={`return-for-revision-${draft.id}`}
                          onClick={() => handleReturnForRevision(draft.id)}
                          disabled={returningDraftId === draft.id}
                          className="text-amber-400 hover:text-amber-300 text-xs px-2 py-1.5 rounded-lg hover:bg-slate-700 flex items-center gap-1 border border-slate-600 disabled:opacity-50"
                          title="Unlock this draft so the student can keep revising"
                        >
                          {returningDraftId === draft.id
                            ? <Loader2 size={14} className="animate-spin" />
                            : <ArrowLeft size={14} />} Return for revision
                        </button>
                      )}
                      <select
                        aria-label={`Reset stage for ${studentLabel(draft)}`}
                        value=""
                        disabled={resettingStageId === draft.id}
                        onChange={(e) => {
                          if (e.target.value) handleResetStage(draft.id, e.target.value);
                        }}
                        className="bg-slate-700 border border-slate-600 text-slate-300 text-xs rounded-lg px-2 py-1.5 cursor-pointer"
                      >
                        <option value="">Reset Stage...</option>
                        {['prewrite', 'draft', 'revise', 'edit', 'publish'].map(s => (
                          <option key={s} value={s}>{STAGE_LABELS[s]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Teacher Feedback */}
                  <div className="border-t border-slate-700 pt-3">
                    <textarea
                      aria-label="Teacher feedback for this student's draft"
                      rows={2}
                      placeholder="Add feedback for this student..."
                      value={feedbackState[draft.id] || ''}
                      onChange={(e) => setFeedbackState(prev => ({ ...prev, [draft.id]: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm resize-y"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        {draft.teacher_feedback_at && (
                          <span className="text-slate-500 text-xs">Feedback saved {new Date(draft.teacher_feedback_at).toLocaleString()}</span>
                        )}
                      </div>
                      <DemoChip label="Sign in to save feedback">
                        <button
                          onClick={() => handleSaveFeedback(draft.id)}
                          disabled={savingFeedbackId === draft.id}
                          className="bg-flow-blue/20 text-flow-blue-hover hover:bg-flow-blue/30 rounded-md px-3 py-1 text-xs font-medium disabled:opacity-50"
                        >
                          {savingFeedbackId === draft.id ? 'Saving...' : 'Save Feedback'}
                        </button>
                      </DemoChip>
                    </div>
                  </div>
                  {/* Coach transcript (Q10) — read-only, RLS-scoped */}
                  <CoachTranscript draftId={draft.id} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Accommodations Manager */}
        <AccommodationsPanel studentDrafts={assignmentDrafts} />

        {trackDraftId && (
          <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-6">
            <div className="bg-slate-800 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
              <DraftTrack draftId={trackDraftId} onClose={() => setTrackDraftId(null)} />
            </div>
          </div>
        )}

        {reportDraftId && (
          <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-6">
            <div className="bg-slate-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <AuthenticityReport draftId={reportDraftId} onClose={() => setReportDraftId(null)} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="text-teal-flow" size={28} />
            Assignments
          </h2>
          <p className="text-slate-400 mt-1">
            Manage your writing assignments and view student submissions
          </p>
        </div>
        <button
          data-testid="create-assignment-btn-list"
          onClick={() => setShowCreateModal(true)}
          className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={18} />
          New Assignment
        </button>
      </div>

      {assignments.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
          <p className="text-slate-400 mb-4">Your writing assignments will live here — prompts, due dates, standards, and student drafts.</p>
          <button
            data-testid="empty-create-assignment-manager"
            onClick={() => setShowCreateModal(true)}
            className="btn-teal px-4 py-2 rounded-lg inline-flex items-center gap-2"
          >
            <Plus size={18} />
            Create your first assignment
          </button>
        </div>
      ) : (
        <div className="space-y-3" role="list">
          {assignments.map((assignment) => (
            <motion.div
              key={assignment.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-surface p-5 hover:border-flow-blue/30 cursor-pointer group"
              onClick={() => handleSelectAssignment(assignment)}
              data-testid={`assignment-row-${assignment.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-heading font-semibold text-white group-hover:text-flow-blue-hover">
                    {assignment.title}
                  </h4>
                  <p className="text-slate-400 text-sm mt-1 line-clamp-1">{assignment.prompt}</p>
                  <div className="flex items-center gap-4 mt-2">
                    {assignment.class_id && classesById[assignment.class_id] && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-teal-flow/20 text-teal-flow rounded text-xs">
                        <Users size={12} />
                        {classesById[assignment.class_id]}
                      </span>
                    )}
                    {assignment.due_date && (
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar size={12} />
                        Due: {new Date(assignment.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {assignment.ccss_standards?.length > 0 && (
                      <div className="flex gap-1">
                        {assignment.ccss_standards.slice(0, 3).map((std, i) => (
                          <span key={i} className="badge-ccss">{std}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRight className="text-slate-600 group-hover:text-flow-blue-hover" size={20} />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateAssignmentModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            reloadAssignments();
          }}
        />
      )}
    </div>
  );
};
